<script lang="ts">
export const badgeCls = cva("badge", {
	variants: {
		color: {
			default: null,
			neutral: "badge-neutral",
			primary: "badge-primary",
			secondary: "badge-secondary",
			accent: "badge-accent",
			success: "badge-success",
			error: "badge-error",
			warning: "badge-warning",
			info: "badge-info",
		},
		variant: {
			default: null,
			outline: "badge-outline bg-base-100 !border-border",
			ghost: "badge-ghost",
			soft: "badge-soft",
		},
		size: {
			xs: "badge-xs [&_svg]:icon-xs",
			sm: "badge-sm [&_svg]:icon-sm",
			md: "badge-md [&_svg]:icon-md",
			lg: "badge-lg [&_svg]:icon-lg",

			/** @deprecated */
			xSmall: "badge-xs [&_svg]:icon-xs",

			/** @deprecated */
			small: "badge-sm [&_svg]:icon-sm",

			/** @deprecated */
			medium: "badge-md [&_svg]:icon-md",

			/** @deprecated */
			large: "badge-lg [&_svg]:icon-lg",
		},
	},
	defaultVariants: {
		size: "md",
	},
});

type _props = VariantProps<typeof badgeCls>;

type DeprecatedVariantProps = {
	/**
	 * @deprecated
	 */
	outlined?: boolean;

	/**
	 * @deprecated
	 */
	ghost?: boolean;
};

export interface BadgeProps
	extends BasePrimitiveProps,
		DeprecatedSizeProps,
		DeprecatedSizeVariantProps,
		DeprecatedVariantProps {
	color?: _props["color"];
	variant?: _props["variant"];
	size?: _props["size"];
	close?: boolean;
}

interface Props extends BadgeProps, /* @vue-ignore */ Omit<HTMLAttributes, keyof BadgeProps> {}
</script>

<script setup lang="ts">
import { mdiCloseCircleOutline } from "@mdi/js";
import { cva, type VariantProps } from "class-variance-authority";
import { Primitive, useForwardExpose } from "reka-ui";
import { computed, useAttrs, type HTMLAttributes } from "vue";
import type { BasePrimitiveProps } from "../../lib/base-props";
import {
	convertDeprecatedSize,
	convertDeprecatedSizeVariant,
	type DeprecatedSizeProps,
	type DeprecatedSizeVariantProps,
} from "../../lib/size";
import { cn, htmlAttrs } from "../../lib/utils";
import Icon from "../icon";

defineOptions({ inheritAttrs: false });

const { as = "span", ...props } = defineProps<Props>() as BadgeProps;
const attrs = useAttrs() as HTMLAttributes;

defineEmits<{
	"click:close": [];
}>();

const size = computed(() => {
	if (props.size != null) {
		return props.size;
	}

	return convertDeprecatedSize(props) || convertDeprecatedSizeVariant(props);
});

const variant = computed<Props["variant"] | null>(() => {
	if (props.variant != null) {
		return props.variant;
	}

	if (props.outlined) {
		return "outline";
	}

	if (props.ghost) {
		return "ghost";
	}

	return null;
});

const { forwardRef } = useForwardExpose();
</script>

<template>
	<Primitive
		:ref="forwardRef"
		:as="as"
		:asChild="props.asChild"
		:class="cn(badgeCls({ ...props, size, variant }))"
		v-bind="htmlAttrs(attrs)"
	>
		<slot />
		<Icon
			v-if="close"
			class="shrink-0 cursor-pointer transition-opacity duration-150 hover:opacity-50"
			:size="size ?? 'md'"
			:d="mdiCloseCircleOutline"
			@click.stop.prevent="$emit('click:close')"
			@mousedown.stop
		/>
	</Primitive>
</template>
